import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FiImage, FiFileText, FiSave, FiTag } from "react-icons/fi";
import ApiServices from "../../services/apiservices";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

const categories = [
  "Public Announcement",
  "Tender Notice",
  "Short Level Falls",
  "Publications",
  "Lastest-Update",
];

const CreatePost = () => {
  const navigate = useNavigate();

  const initialValues = {
    title: "",
    category: "",
    introContent: "",
    mainContent: "",
    addtoNavbarMenu: false,
    featuredImage: null,
    contentImage1: null,
    contentImage2: null,
    pdfFile: null,
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    category: Yup.string().required("Please select a category"),
    introContent: Yup.string().when("category", {
      is: (val) => val !== "Tender Notice" && val !== "Map-Meter-Refund",
      then: (schema) => schema.required("Introductory content is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    mainContent: Yup.string().when("category", {
      is: (val) => val !== "Tender Notice" && val !== "Map-Meter-Refund",
      then: (schema) => schema.required("Main content is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    featuredImage: Yup.mixed().when("category", {
      is: (val) => val !== "Tender Notice" && val !== "Map-Meter-Refund",
      then: (schema) => schema.required("Featured image is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    pdfFile: Yup.mixed().when("category", {
      is: (val) => val === "Tender Notice" || val === "Map-Meter-Refund",
      then: (schema) => schema.required("PDF file is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    addtoNavbarMenu: Yup.boolean(),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {    
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("category", values.category);
      // Sending HTML strings directly to DB to preserve bold/italic/paragraphs
      formData.append("introContent", values.introContent || "");
      formData.append("mainContent", values.mainContent || "");
      formData.append("addtoNavbarMenu", values.addtoNavbarMenu);

      if (values.featuredImage instanceof File) formData.append("featuredImage", values.featuredImage);
      if (values.contentImage1 instanceof File) formData.append("contentImage1", values.contentImage1);
      if (values.contentImage2 instanceof File) formData.append("contentImage2", values.contentImage2);
      if (values.pdfFile instanceof File) formData.append("pdfFile", values.pdfFile);
              
      const res = await ApiServices.postRequest("/posts/createpost.php", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });       
      if (res.success) {
        toast.success(res.message);
        resetForm();
        navigate("/admin/posts");
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-4xl mx-auto bg-white p-6 md:p-10 rounded-xl shadow-lg">
        <h2 className="text-2xl md:text-3xl font-bold mb-8 text-gray-800">Create New Post</h2>

        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values, setFieldValue, isSubmitting, setFieldTouched }) => {
            const isTender = values.category === "Tender Notice";
            const isMapMeterRefund = values.category === "Map-Meter-Refund";

            return (
              <Form className="space-y-6">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2"><FiTag /> Category</label>
                  <Field
                    as="select"
                    name="category"
                    className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 outline-none"
                    onChange={(e) => {
                      const value = e.target.value;
                      setFieldValue("category", value);
                      if (value === "Tender Notice" || value === "Map-Meter-Refund") {
                        setFieldValue("featuredImage", null);
                        setFieldValue("introContent", "");
                        setFieldValue("mainContent", "");
                      }
                    }}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                  </Field>
                  <ErrorMessage name="category" component="p" className="text-red-500 text-xs mt-1" />
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Post Title</label>
                  <Field name="title" placeholder="Enter post title" className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-red-500 outline-none" />
                  <ErrorMessage name="title" component="p" className="text-red-500 text-xs mt-1" />
                </div>

                {!isTender && !isMapMeterRefund && (
                  <>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium mb-2"><FiImage /> Featured Image</label>
                      <input type="file" accept="image/*" onChange={(e) => setFieldValue("featuredImage", e.currentTarget.files[0])} className="w-full border rounded-lg px-4 py-2" />
                      <ErrorMessage name="featuredImage" component="p" className="text-red-500 text-xs mt-1" />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">Introductory Content</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={values.introContent}
                        onChange={(event, editor) => setFieldValue("introContent", editor.getData())}
                        onBlur={() => setFieldTouched("introContent", true)}
                      />
                      <ErrorMessage name="introContent" component="p" className="text-red-500 text-xs mt-1" />
                    </div>

                    <div>
                      <label className="text-sm font-medium mb-2 block">Main Content</label>
                      <CKEditor
                        editor={ClassicEditor}
                        data={values.mainContent}
                        onChange={(event, editor) => setFieldValue("mainContent", editor.getData())}
                        onBlur={() => setFieldTouched("mainContent", true)}
                      />
                      <ErrorMessage name="mainContent" component="p" className="text-red-500 text-xs mt-1" />
                    </div>
                  </>
                )}

                <div>
                  <label className="flex items-center gap-2 text-sm font-medium mb-2"><FiFileText /> {isTender || isMapMeterRefund ? "Upload Required PDF" : "Upload PDF (Optional)"}</label>
                  <input type="file" accept="application/pdf" onChange={(e) => setFieldValue("pdfFile", e.currentTarget.files[0])} className="w-full border rounded-lg px-4 py-2" />
                  <ErrorMessage name="pdfFile" component="p" className="text-red-500 text-xs mt-1" />
                </div>

                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={values.addtoNavbarMenu} onChange={(e) => setFieldValue("addtoNavbarMenu", e.target.checked)} className="w-5 h-5" />
                  <label className="text-sm font-medium">Add link on Navbar</label>
                </div>

                <button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition disabled:bg-gray-400 flex items-center justify-center gap-2 font-semibold">
                  <FiSave /> {isSubmitting ? "Saving..." : "Save Post"}
                </button>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default CreatePost;